import { html, render } from "lit-html";
import TextGrid from "overprint/overprint/text-grid";
import Font from "overprint/overprint/font";
import Cell from "overprint/overprint/cell";
import { plotLine } from "flatland/src/line";
import { Slots } from "./equip-dialog";
import { Input } from "./input";
import Glyphs from "../glyphs";
import Palette from "../palette";

const glyphCache = new Map();

const InputMode = {
  Exploring: 0,
  Looking: 1,
  Targeting: 2,
  Equipping: 3
}

class AbstractCursor {
  constructor(position) {
    this.x = position.x;
    this.y = position.y;
  }
}

class TargetingCursor extends AbstractCursor {
  isValidTarget = false;

  handleMovement(direction, stage) {
    const tx = this.x + direction.x;
    const ty = this.y + direction.y;

    // TODO: modulate by weapon distance
    if (stage.isVisible(tx, ty) && !(tx == stage.player.x && ty == stage.player.y)) {
      this.x = tx;
      this.y = ty;
    }

    this.isValidTarget = stage.canMoveTo(tx, ty);
  }
}

class LookingCursor extends AbstractCursor {
  handleMovement(direction, stage) {
    const tx = this.x + direction.x;
    const ty = this.y + direction.y;

    if (stage.isSeen(tx, ty) && !(tx == stage.player.x && ty == stage.player.y)) {
      this.x = tx;
      this.y = ty;
    }
  }
}

class ScreenHud {
  constructor() {
    this.container = document.querySelector(".game-hud");
  }

  getHudTemplate(stage) {
    const player = stage.player;
    const enemies = stage.visibleEntities().filter(en => en.isEnemy());
    const hpPercentage = `width:${player.health.percentage}%;`;
    const location = stage.map[player.y][player.x].description;
    const shortLog = stage.adventure.log.slice(0, 30);

    return html`
      <div class="hud-group">
        <h2>${player.epithet}</h2>

        <div class="hud-meter">
          <p>Health <span>${player.health.hp}/${player.health.max}</span></p>
          <div class="progress health">
            <div class="progress-value" style=${hpPercentage}></div>
          </div>
        </div>
        <div class="hud-stats">
          <p class="stat">Strength <span>${"•".repeat(player.stats.strengthModifier)}</span></p>
          <p class="stat">Agility <span>${"•".repeat(player.stats.agilityModifier)}</span></p>
          <p class="stat">Toughness <span>${"•".repeat(player.stats.toughnessModifier)}</span></p>
        </div>
      </div>

      <div class="hud-group">
        <h2>${stage.adventure.getLevel(stage.levelId).screenName}</h2>
        ${enemies.map(en => {
          const _hpPercentage = `width:${en.health.percentage}%;`;
          const glyphStyle= `color:${en.glyph.foregroundColor};`;
          const glyphChar = en.glyph.character;

          return html`
            <div class="hud-meter">
              <p><span style=${glyphStyle}>${glyphChar}</span> ${en.name}</p>
              <div class="progress progress-mini health">
                <div class="progress-value" style=${_hpPercentage}></div>
              </div>
            </div>
          `;
        })}
      </div>

      <div class="hud-group">
        <h2>Log</h2>

        <div class="hud-log">
          ${shortLog.map(entry => {
            return html`
              <p>${entry.message}</p>
            `;
          })}
        </div>
      </div>
    `;
  }

  render(stage) {
    render(this.getHudTemplate(stage), this.container);
  }
}

class Screen {
  constructor() {
    this.width = 40;
    this.height = 26;
    this.canvas = document.querySelector(".game-stage");
    this.hud = new ScreenHud();
    this.viewportWidth = document.documentElement.clientWidth;
    this.camera = {
      x: 0,
      y: 0
    }
    this.mode = InputMode.Exploring;
    this.lastMove = Input.MoveUp;
    this.rescale();

    window.screen = this;
  }

  activate(context) {
    return;
  }

  deactivate(context) {
    return;
  }

  handleInput(key, stage) {
    switch(key) {
      case "p":
        console.log(`Location: {x: ${stage.player.x}, y: ${stage.player.y}}`);
        console.log(stage.player);
      break;
      case "k":
      case "ArrowUp":
        return this.handleMovement(Input.MoveUp, stage);
      break;
      case "j":
      case "ArrowDown":
        return this.handleMovement(Input.MoveDown, stage);
      break;
      case "h":
      case "ArrowLeft":
        return this.handleMovement(Input.MoveLeft, stage);
      break;
      case "l":
      case "ArrowRight":
        return this.handleMovement(Input.MoveRight, stage);
      break;
      case ".":
      case "r":
        return Input.Rest;
      break;
      case "z":
      case "?":
        this.activateLookingMode(stage);
      break;
      case "t":
      case "f":
        this.activateTargetingMode(stage);
      break;
      case "Enter":
      //case "Space":
        if (this.mode == InputMode.Looking) {
          return this.lookCommand(stage);
        } else if (this.mode == InputMode.Targeting) {
          return this.attackRangedTargetCommand(stage);
        }
      break;
      case " ":
      case "q":
        if (this.mode != InputMode.Exploring) {
          this.deactivateCursorMode();
        } else {
          return Input.Help;
        }
      break;
      case "e":
      case "i":
      case "w":
      case "a":
        // TODO: if i,w,a are pressed then activate selection on open
        return Input.Equip;
      break;
      case "/":
        return Input.Help;
      break;
      case "g":
        return this.getCommand(stage);
      break;
      case "d":
        return this.dropCommand(stage);
      break;
    }
    return false;
  }

  attackRangedTargetCommand(stage) {
    this.mode = InputMode.Exploring;
    return {
      ...Input.RangedAttack,
      detail: {
        target: {
          x: this.cursor.x,
          y: this.cursor.y
        }
      }
    }
  }

  getCommand(stage) {
    const pile = stage.entitiesAt(stage.player.x, stage.player.y).filter(entity => entity.isItem());

    if (pile.length < 1) return false;

    return {
      ...Input.Get,
      detail: pile
    }
  }

  dropCommand(stage) {
    return {
      ...Input.Drop,
      detail: {
        slot: Slots.NONE,
        index: 0
      }
    }
  }

  lookCommand(stage) {
    // TODO: position dialog on screen by tile location
    // screen: this.cellToPx(
    //   this.cursor.x - this.camera.x,
    //   this.cursor.y - this.camera.y
    // )
    return {
      ...Input.Examine,
      detail: stage.lookAt(this.cursor.x, this.cursor.y)
    }
  }

  cellToPx(rowPos, colPos) {
    // TODO: really should not be reaching into the internals of the grid class
    // const cellWidth = this.grid._cellWidth * this.grid._ratio;
    // const cellHeight = this.grid._cellHeight * this.grid._ratio;
    const cellWidth = this.grid._cellWidth;
    const cellHeight = this.grid._cellHeight;
    const x = Math.round(rowPos * cellWidth);
    const y = Math.round(colPos * cellHeight);
    return { x, y, cellWidth, cellHeight }
  }

  handleMovement(input, stage) {
    switch (this.mode) {
      case InputMode.Exploring:
        this.lastMove = input;
        return input;
      break;
      case InputMode.Looking:
      case InputMode.Targeting:
        this.cursor.handleMovement(input, stage);
        return false;
      break;
    }
  }

  activateTargetingMode(stage) {
    if (stage.player.isRangedWeaponEquipped()) {
      this.mode = InputMode.Targeting;

      this.cursor = new TargetingCursor({
        x: stage.player.x + this.lastMove.x,
        y: stage.player.y + this.lastMove.y
      });
    }
  }

  activateLookingMode(stage) {
    this.mode = InputMode.Looking;

    this.cursor = new LookingCursor({
      x: stage.player.x + this.lastMove.x,
      y: stage.player.y + this.lastMove.y
    });
  }

  deactivateCursorMode() {
    this.mode = InputMode.Exploring;
    this.cursor = false;
  }

  // Aspect ratio hardcoded, see CSS
  //
  // 20 : 13 (40 : 26)
  // standard screen:
  //    w 960 : 480 = 1440 (75%, 25%)
  //    h 624
  get viewportFontSize() {
    return Math.round(this.viewportWidth / 90 * 1.5 + 1);
  }

  rescale() {
    this.viewportWidth = document.documentElement.clientWidth;
    this.grid = new TextGrid(this.canvas, {
      width: this.width,
      height: this.height,
      emptyCell: Glyphs.Void,
      font: Font("JuliaMono", false, this.viewportFontSize)
    });
  }

  updateCamera(x, y, mapWidth, mapHeight) {
    let offsetX = Math.floor(x - (this.width / 2));
    offsetX = Math.min(offsetX, mapWidth - this.width);
    offsetX = Math.max(0, offsetX);

    let offsetY = Math.floor(y - (this.height / 2));
    offsetY = Math.min(offsetY, mapHeight - this.height);
    offsetY = Math.max(0, offsetY);

    // TODO: this should be a rect rather than a point
    this.camera = {
      x: offsetX,
      y: offsetY
    }
}

  renderMap(stage) {
    for (let y=0; y < this.height; y++) {
      for (let x=0; x < this.width; x++) {
        let tileType = Glyphs.Void;
        let cx = x + this.camera.x;
        let cy = y + this.camera.y;

        // Hardcoded FOV code until tile lighting is decided
        if (stage.isVisible(cx, cy)) {
          try {
            tileType = Glyphs[stage.map[cy][cx].name];
          } catch(err) {
            console.log(stage.map)
            console.log(stage.map[cy])
            console.log(stage.map[cy][cx])
            console.log(cy, cx)
            throw new Error("bad name")
          }
          tileType = Glyphs[stage.map[cy][cx].name];

          const occupants = stage.entitiesAt(cx, cy);

          if (occupants.length == 1) {
            tileType = this.overprintEntity(occupants[0], tileType)
          } else if (occupants.length > 1) {
            let entity = occupants.find(ent => ent.isBlocking());
            // TODO: represent piles rather than draw the topmost item
            if (!entity) entity = occupants[occupants.length - 1];
            tileType = this.overprintEntity(entity, tileType);
          }

        } else if (stage.isSeen(cx, cy)) {
          if (stage.inOverworld()) {
            try {
              tileType = Glyphs[stage.map[cy][cx].name];
            } catch(err) {
              console.log(stage.map)
              console.log(stage.map[cy])
              console.log(stage.map[cy][cx])
              console.log(cy, cx)
              throw new Error("bad name")
            }
            tileType = Glyphs[stage.map[cy][cx].name];
          } else {
            // Hack to mute tiles outside FOV in underworld
            //
            // Need to fix Overprint to be much more explicit about comparing
            // cells by value equality vs
            //
            // This doesn’t provide much of an improvement to the double-buffer
            // rendering architecture but does prevent a lot of GC churn through
            // avoiding creating lots of little duplicate objects.
            let glyph = Glyphs[stage.map[cy][cx].name];

            let glyphCacheKey = `${glyph.character}|${glyph.foregroundColor}|${Glyphs.Void.backgroundColor}`;

            if (!glyphCache.has(glyphCacheKey)) {
              tileType = Cell(
                glyph.character,
                glyph.foregroundColor,
                Glyphs.Void.backgroundColor
              );
              glyphCache.set(glyphCacheKey, tileType);
            } else {
              tileType = glyphCache.get(glyphCacheKey);
            }
          }
        }

        this.grid.writeCell(x, y, tileType);
      }
    }
  }

  overprintEntity(entity, tile) {
    let glyph = entity.glyph;
    if (glyph.backgroundColor != tile.backgroundColor) {
      // Need to rebind the object id to blow the ref cache
      glyph = Cell(
        glyph.character,
        glyph.foregroundColor,
        tile.backgroundColor
      );
    }
    return glyph;
  }

  renderEntities(stage) {
    for (let entity of stage.entities) {
      let cx = entity.x - this.camera.x;
      let cy = entity.y - this.camera.y;
      if (stage.isVisible(entity.x, entity.y)) {
        let glyph = entity.glyph;
        let tile = Glyphs[stage.map[entity.y][entity.x].name];
        if (glyph.backgroundColor != tile.backgroundColor) {
          // Need to rebind the object id to blow the ref cache
          glyph = Cell(
            glyph.character,
            glyph.foregroundColor,
            tile.backgroundColor
          );
        }
        this.grid.writeCell(cx, cy, glyph);
      }
    }
  }

  renderDebug(stage) {
    let xline = `x: ${stage.player.x}`.split("");
    let yline = `y: ${stage.player.y}`.split("");

    let xpos = 0;
    for (let xchar of xline) {
      this.grid.writeCell(xpos, 0, Cell(
        xchar,
        Glyphs.Ocean.foregroundColor
      ));
      xpos++;
    }

    let ypos = 0;
    for (let ychar of yline) {
      this.grid.writeCell(ypos, 1, Cell(
        ychar,
        Glyphs.Ocean.foregroundColor
      ));
      ypos++;
    }

    let label = "Health:".split("");
    let hpos = 0;
    for (let hchar of label) {
      this.grid.writeCell(hpos, 2, Cell(
        hchar,
        Glyphs.Ocean.foregroundColor
      ));
      hpos++;
    }

    this.grid.writeCell(8, 2, Cell(
      stage.player.health.hp.toString(),
      Glyphs.Ocean.foregroundColor
    ));
  }

  renderCursor(stage) {
    if (this.mode == InputMode.Looking) {
      this.grid.writeCell(
        this.cursor.x - this.camera.x,
        this.cursor.y - this.camera.y,
        Glyphs.UI.LookCursor
      );
    } else if (this.mode == InputMode.Targeting) {
      // TODO: should cursor store vec?
      const targetPos = {
        x: this.cursor.x,
        y: this.cursor.y
      }

      if (!this.cursor.isValidTarget) {
        this.grid.writeCell(
          targetPos.x - this.camera.x,
          targetPos.y - this.camera.y,
          Glyphs.UI.TargetCursorBlank
        );
        return;
      }

      const playerPos = {
        x: stage.player.x,
        y: stage.player.y
      }

      const [, ...targetTrail] = plotLine(playerPos, targetPos);
      let hasClearLos = true;

      for (const trail of targetTrail) {
        if (stage.isPartiallyCovered(trail.x, trail.y)) {
          hasClearLos = false;
          break;
        }

        let glyph = Glyphs.UI.TargetCursorTrail;
        let tile = Glyphs[stage.map[trail.y][trail.x].name];
        if (glyph.backgroundColor != tile.backgroundColor) {
          // Need to rebind the object id to blow the ref cache
          glyph = Cell(
            glyph.character,
            glyph.foregroundColor,
            tile.backgroundColor
          );
        }

        this.grid.writeCell(
          trail.x - this.camera.x,
          trail.y - this.camera.y,
          glyph
        );
      }

      this.grid.writeCell(
        targetPos.x - this.camera.x,
        targetPos.y - this.camera.y,
        hasClearLos ? Glyphs.UI.TargetCursorValid : Glyphs.UI.TargetCursorBlocked
      );
    }
  }

  render(stage) {
    if (stage.isLocked()) return;

    this.updateCamera(
      stage.player.x,
      stage.player.y,
      stage.width,
      stage.height
    );

    this.renderMap(stage);

    this.renderCursor(stage);

    this.grid.render();

    // Redraw the HUD (this should probably not run on frameloop but meh)
    this.hud.render(stage);
  }
}

export default Screen;
